
import { createTheme, SimplePaletteColorOptions } from '@mui/material'

const PRIMARY_COLOR_MAIN = 'rgba(21, 96, 90, 1)'

export const BACKGROUND_COLOR_MAIN = '#E8EDEB'
export const BACKGROUND_COLOR_LIGHT = '#F3F6F5'

const SECONDARY_COLOR_MAIN = 'rgba(204, 234, 210, 1)'

const textColorMain = 'rgba(0, 53, 57, 0.97)'
const textColorMainLight = 'rgba(0, 76, 82, 0.6)'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    display1: React.CSSProperties
    display2: React.CSSProperties
    body1S: React.CSSProperties
    body1B?: React.CSSProperties
    body2S: React.CSSProperties
    body2B: React.CSSProperties
    icon: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    display1?: React.CSSProperties
    display2?: React.CSSProperties
    body1S?: React.CSSProperties
    body1B?: React.CSSProperties
    body2S?: React.CSSProperties
    body2B?: React.CSSProperties
    icon?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h5: false;
    h6: false;
    display1: true
    display2: true
    body1S: true
    body2S: true
    body1B: true
    body2B: true
    icon: true
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    link: SimplePaletteColorOptions;
  }
  interface PaletteOptions {
    link: SimplePaletteColorOptions;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    link: true;
  }
  interface ButtonPropsVariantOverrides {
    icon: true;
  }
}




const theme = createTheme({
  palette: {
    common: {
      black: '#000',
      white: '#fff'
    },
    text: {
      primary: textColorMain,
      secondary: textColorMainLight,
      disabled: 'rgba(0, 76, 82, 0.38)'
    },
    primary: {
      main: PRIMARY_COLOR_MAIN,
      light: '#437F7B',
      dark: '#0E433E',
    },
    secondary: {
      main: SECONDARY_COLOR_MAIN,
      light: '#E1F9E6',
      dark: '#AEDFB8',
      contrastText: 'rgba(0, 53, 57, 0.97)'
    },
    error: {
      main: '#FF6555',
      light: '#FF8578',
      dark: '#D54F41',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#51C0F0',
      light: '#A8DFF8',
      dark: '#0CA8EC',
      contrastText: '#FFFFFF',
      '200': '#DCF2FC'
    },
    warning: {
      main: '#FFC121',
      light: '#FFCE51',
      dark: '#EB8813',
      contrastText: '#FFFFFF'
    },
    success: {
      main: '#35D655',
      light: '#5DDE77',
      dark: '#25953B',
      contrastText: '#FFFFFF'
    },
    action: {
      active: 'rgba(21, 96, 90, 0.54)',
      hover: 'rgba(21, 96, 90, 0.04)',
      disabled: 'rgba(21, 96, 90, 0.26)',
      disabledBackground: 'rgba(21, 96, 90, 0.12)',
      focus: 'rgba(21, 96, 90, 0.12)'
    },

    background: {
      default: '#F3F7F7',
      paper: '#FFFFFF'
    },
    link: {
      main: '#0D99FF'
    },
    grey: {
      '50': '#F8FBFB',
      '100': '#F1F7F6',
      '200': '#E6F1F1',
      '300': '#D6E5E3',
      '500': '#94A3A1',
      '600': '#6B7A78',
      '800': '#384745',
      '900': '#1A2423'
    }
  },
  typography: {
    fontFamily: [
      'Lexend Deca'
    ].join(','),

    display1: {
      fontSize: 36,
    },
    display2: {
      fontSize: 32,
    },
    h1: {
      fontSize: 24,
      fontWeight: 500,
      letterSpacing: '-1.5px'
    },
    h2: {
      fontSize: 20,
      fontWeight: 500,
      letterSpacing: '-0.5px'
    },
    h3: {
      fontSize: 16,
      fontWeight: 500,
      letterSpacing: '0px'
    },
    h4: {
      fontSize: 14,
      fontWeight: 500,
      letterSpacing: '0.25px'
    },
    // Disable h5 & h6 variant
    h5: undefined,
    h6: undefined,

    body1: {
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: '0.15px'
    },
    body2: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: '0.15px'
    },
    body1B: {
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: '0.15px'
    },
    body1S: {
      fontSize: 14,
      fontWeight: 300,
      letterSpacing: '0.15px'
    },
    body2S: {
      fontSize: 12,
      fontWeight: 300,
      letterSpacing: '0.15px'
    },
    body2B: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.15px'
    },
    icon: {
      fontSize: 24,
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 400,
      letterSpacing: '0.25px'
    },
    subtitle2: {
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: '0.25px'
    },
    overline: {
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: '0.5px'
    },
    caption: {
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: '0.5px'
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':disabled': {
          color: textColorMainLight,
          WebkitTextFillColor: `${textColorMainLight} !important`,
          opacity: .5,
        },
        '& .Mui-disabled': {
          opacity: .5,
        },
        '& .PrivateSwitchBase-input': {
          opacity: 0
        },
        '& .MuiSwitch-switchBase': {
          opacity: 1
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:first-child:before': {
            display: 'none'
          }
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minWidth: 0
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
       select: {
          '&.Mui-disabled': {
            'color': '#80A5A8',
            'webkkitTextFillColor': '#80A5A8'
          }
       }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#0D99FF',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: textColorMain,
          fontSize: 14,
          fontWeight: 400,
          opacity: .6
        },
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          '::placeholder': {
            color: SECONDARY_COLOR_MAIN,
          },
          '&.Mui-disabled': {
            'color': textColorMainLight,
            'webkkitTextFillColor': textColorMainLight
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: textColorMain,
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            'opacity': 1
          }
        },
        thumb: {
          opacity: 1
        },
        track: {
          opacity: 1
        },
      }
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'icon' },
          style: {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: SECONDARY_COLOR_MAIN,
            },

          }
        },
      ],
      styleOverrides: {
        root: {
          minWidth: 32,
          textTransform: 'none',
          boxShadow: 'none',
          ':hover': {
            boxShadow: 'none',
          },
          ':disabled': {
            backgroundColor: 'unset',
            opacity: .5,
          },
        },
        outlined: {
          ':hover': {
            backgroundColor: SECONDARY_COLOR_MAIN,
          }
        },
        text: {
          color: textColorMain,
          ': hover': {
            backgroundColor: SECONDARY_COLOR_MAIN,
          },
          ':disabled': {
            opacity: .5,
            color: textColorMain,
            WebkitTextFillColor: `${textColorMain} !important`,
          }
        },
        startIcon: {
          marginRight: 10,
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 400
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          fontSize: 14,
          fontWeight: 400
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 300,
          '&:hover': {
            backgroundColor: 'rgba(21, 96, 90, 0.04)'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 400,
          color: textColorMain,
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: textColorMain,
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0px 3px 6px rgba(137, 175, 172, 0.2)'
        }
      }
    }
  }
})



export default theme
